/**
 * Shared page heading / page title utilities.
 *
 * `getPageTitle(pathname)`   – returns the full string for `document.title`
 *                              (e.g. "Portfolio - Scheme Details | MFCentral").
 * `getPageHeading(pathname)` – returns a concise label suitable for the
 *                              page's single `<h1>` element
 *                              (e.g. "Scheme Details").
 *
 * Both helpers share the same `titleTokenMap` so heading and title stay
 * in sync as routes are added or renamed.
 */

const titleTokenMap: Record<string, string> = {
    // Main sections
    portal: 'Dashboard',
    links: 'Quick Links',
    investor: 'Investor Login',

    // Auth routes
    signin: 'Sign In',
    signup: 'Sign Up',
    'set-password': 'Set Password',
    'verify-password': 'Verify Password',
    'reset-password': 'Reset Password',
    'signup-otp': 'Sign Up OTP Verification',
    'forgot-password': 'Forgot Password',
    'forgot-password-otp': 'Forgot Password OTP',
    'forgot-password-questionnaire': 'Forgot Password Security Questions',
    'signin-questionnaire': 'Sign In Security Questions',
    'reset-question-otp': 'Reset Security Question OTP',
    'signup-questionnaire': 'Signup Questionnaire',
    'reset-questionnaire': 'Reset Questionnaire',
    stack: 'Reset PIN',

    // Dashboard routes
    home: 'Home',
    portfolio: 'Portfolio',
    profile: 'Profile',
    'update-security-questions': 'Update Security Questions',
    'update-security-questions-otp': 'Update Security Questions OTP',
    'update-security-questions-success': 'Security Questions Updated',
    'unregistered-folios': 'Unregistered Folios',
    'unregistered-folios-otp': 'Unregistered Folios OTP',
    'unregistered-folios-success': 'Unregistered Folios Success',
    'unclaimed-payout': 'Unclaimed Payout',
    'sebi-cas': 'SEBI CAS',
    crm: 'CRM Dashboard',
    depreq: 'Depository Request',
    'depreq-success': 'Depository Request Success',
    'kyc-status': 'KYC Status',
    'track-for-nct': 'Track NCT Request',
    'track-for-transaction': 'Track Transaction',
    'portfolio-scheme-details': 'Portfolio Scheme Details',
    track: 'Track Request',
    commercial: 'Commercial',
    nct: 'NCT',

    // Update PAN
    'update-pan': 'Update PAN',
    'update-pan-otp': 'Update PAN OTP',
    'update-pan-list': 'Update PAN Folios',
    'update-pan-selected': 'Update PAN Selected Folios',
    'update-pan-success': 'PAN Updated Successfully',

    // Transmission Request
    'transmission-request': 'Transmission Request',
    'transmission-download-list': 'Transmission Download List',
    'transmission-request-otp-mobile': 'Transmission OTP (Mobile)',
    'transmission-request-otp-email': 'Transmission OTP (Email)',
    'transmission-request-holder': 'Transmission Request Holder',
    'transmission-request-pan': 'Transmission Request PAN',
    'transmission-request-success': 'Transmission Request Success',
    'transmission-download': 'Transmission Download',
    'transmission-download-otp': 'Transmission Download OTP',
    'transmission-pan': 'Transmission PAN',
    'transmission-list': 'Transmission List',
    'transfer-of-units': 'Transfer Of Units',

    // Change Email
    'change-email': 'Change Email',
    'change-email-otp': 'Change Email OTP',
    'change-email-list': 'Change Email Folios',
    'change-email-success': 'Email Changed Successfully',
    'change-email-document': 'Change Email Document',
    'change-email-auth': 'Change Email Authentication',

    // Change Mobile
    'change-mobile': 'Change Mobile',
    'change-mobile-auth': 'Change Mobile Authentication',
    'change-mobile-document': 'Change Mobile Document',
    'change-mobile-new': 'Change Mobile Details',
    'otp-verification': 'OTP Verification',
    'change-mobile-success': 'Mobile Changed Successfully',

    // Change Dividend
    'change-dividend': 'Change Dividend Option',
    'change-dividend-otp': 'Change Dividend OTP',
    'change-dividend-success': 'Dividend Option Changed',

    // Update Nominee
    'update-nominee': 'Update Nominee',
    'update-nominee-edit': 'Edit Nominee Details',
    'update-nominee-edit-otp': 'Update Nominee OTP',
    'update-nominee-success': 'Nominee Updated Successfully',
    'update-nominee-failed': 'Nominee Update Failed',

    // Change Bank
    'change-bank': 'Change Bank',
    'change-bank-selected': 'Change Bank Selected Folios',
    'change-bank-otp': 'Change Bank OTP',
    'change-bank-document': 'Change Bank Document',
    'change-bank-success': 'Bank Changed Successfully',

    // Consolidation Folios
    'consolidation-folios': 'Consolidate Folios',
    'consolidation-folios-otp': 'Consolidation OTP',
    'consolidation-folios-success': 'Folios Consolidated Successfully',
    'consolidation-folios-failed': 'Folios Consolidation Failed',
    'consolidate-source-folios': 'Consolidate Source Folios',

    // Change Tax Status
    'change-tax': 'Change Tax Status',
    'change-tax-upload': 'Change Tax Document Upload',
    'change-tax-otp': 'Change Tax Status OTP',
    'change-tax-success': 'Tax Status Changed Successfully',
    'tax-bank-details': 'Tax Bank Details',

    // Update Form 15
    'update-form15': 'Update Form 15',
    'update-form15-otp': 'Form 15 OTP',
    'update-form15-list': 'Form 15 Folios',
    'update-form15-income': 'Form 15 Income Details',
    'update-form15-success': 'Form 15 Updated Successfully',

    // Minor Status
    'minor-status': 'Minor to Major Status Update',
    'minor-status-nominee': 'Minor Status Nominee',
    'minor-status-pan': 'Minor Status PAN',
    'minor-status-review': 'Minor Status Review',
    'minor-status-list': 'Minor Status Folios',
    'minor-status-edit-bank': 'Minor Status Bank Details',
    'minor-status-otp': 'Minor Status OTP',
    'minor-status-success': 'Minor Status Updated Successfully',

    // Correction Folios
    'corrections-folios': 'Correct Folios',
    'correction-folios-selection': 'Select Folios for Correction',
    'correction-folios-otp': 'Folios Correction OTP',
    'correction-folios-success': 'Folios Corrected Successfully',
    'correction-folios-failed': 'Folios Correction Failed',

    // Update IFSC
    'update-ifsc': 'Update IFSC Code',
    'update-ifsc-details': 'Update IFSC Details',
    'update-ifsc-otp': 'Update IFSC OTP',
    'update-ifsc-success': 'IFSC Updated Successfully',

    // Bank Mandate
    'bank-mandate': 'Bank Mandate',
    'bank-mandate-selected': 'Bank Mandate Selected',
    'bank-mandate-otp': 'Bank Mandate OTP',
    'bank-mandate-delete-selected': 'Delete Bank Mandate',
    'bank-mandate-add-bank': 'Add Bank for Mandate',
    'bank-mandate-success': 'Bank Mandate Updated',
    'bank-mandate-failed': 'Bank Mandate Failed',

    // FATCA Declaration
    'fatca-declaration': 'FATCA Declaration',
    'fatca-declaration-otp': 'FATCA Declaration OTP',
    'fatca-declaration-success': 'FATCA Declaration Submitted',

    // Lock / Unlock Folios
    'lock-unlock-folios': 'Lock or Unlock Folios',
    'lock-unlock-folios-otp': 'Lock or Unlock Folios OTP',
    'lock-unlock-folios-success': 'Folios Lock Status Updated',

    // Change IDCW
    'change-idcw': 'Change IDCW Option',
    'change-idcw-otp': 'Change IDCW OTP',
    'change-idcw-success': 'IDCW Option Changed',

    // Commercial Actions
    actions: 'Actions',
    'additional-purchase': 'Additional Purchase',
    'ap-investment': 'Additional Purchase Investment',
    ap: 'Additional Purchase',
    'mf-cart': 'MF Cart',
    'mf-failure': 'Transaction Failed',
    redemption: 'Redemption',
    switch: 'Switch',
    stp: 'STP',
    swp: 'SWP',
    sip: 'SIP',

    // Quick Links
    faq: 'FAQ',
    'faq-section': 'FAQ Section',
    'privacy-policy': 'Privacy Policy',
    'terms-of-use': 'Terms of Use',
    'amfi-cas': 'AMFI CAS',
    'reset-pin': 'Reset PIN',

    // Inactive Folios
    'inactive-folios': 'Inactive Folios',
    'inactive-folios-search': 'Search Inactive Folios',
    'inactive-folios-track': 'Track Inactive Folios Request',
    'inactive-folios-go-track': 'Track Inactive Folios',
    'inactive-folios-summary': 'Inactive Folios Summary',
    search: 'Search',
    summary: 'Folio Summary',
    mitra: 'Mitra Login',

    // Explore MFs
    purchase: 'Purchase',
    explore: 'Explore Mutual Funds',
    invest: 'Invest in Mutual Fund',
    'payment-method': 'Payment Method',
    'profile-details': 'Profile Details',
    'verify-otp': 'Verify OTP',
    'edit-nominee': 'Edit Nominee',
    'edit-bank': 'Edit Bank Details',
    'edit-fatca': 'Edit FATCA Details',
    'payment-status': 'Payment Status',
    'prospect-payment-status': 'Prospect Payment Status',
    'otm-payment-status': 'OTM Payment Status',
};

export const titleize = (token: string): string => {
    if (titleTokenMap[token]) {
        return titleTokenMap[token];
    }
    const normalized = token.replace(/-/g, ' ');
    return normalized
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const isRouteParameter = (segment: string): boolean => {
    return (
        /^[0-9]+$/.test(segment) ||
        /^[0-9a-fA-F]{8,}$/.test(segment) ||
        /^[A-Z0-9]{8,}$/.test(segment)
    );
};

const cleanPathname = (pathname: string): string =>
    pathname.split('?')[0].split('#')[0].replace(/\/+$/g, '').replace(/^\//, '');

const getMeaningfulSegments = (pathname: string): string[] => {
    const cleanPath = cleanPathname(pathname);
    if (!cleanPath) return [];
    return cleanPath.split('/').filter(Boolean);
};

/**
 * Returns the value used for `document.title` for the given route.
 * Format: "<section> - <leaf> | MFCentral".
 */
export const getPageTitle = (pathname: string): string => {
    const segments = getMeaningfulSegments(pathname);
    if (segments.length === 0) {
        return 'Dashboard | MFCentral';
    }
    const titleSegments = segments
        .filter((segment, index) => {
            if (index === 0) return true;
            return !isRouteParameter(segment);
        })
        .map((segment, index) => {
            if (index === 0 && segment === 'portal') {
                return titleTokenMap.portal;
            }
            if (index === 0 && segment === 'links') {
                return titleTokenMap.links;
            }
            return titleize(segment);
        });
    if (titleSegments.length === 0) {
        return 'Dashboard | MFCentral';
    }
    return `${titleSegments.join(' - ')} | MFCentral`;
};

/**
 * Returns a concise label for the page's single visible/sr-only `<h1>`.
 *
 * Resolution order:
 *   1. The last non-parameter segment that is NOT a top-level section
 *      (e.g. `/portal/nct/change-bank` → "Change Bank").
 *   2. If only the section is present (e.g. `/portal`) → the section name.
 *   3. Empty path → "Dashboard".
 */
export const getPageHeading = (pathname: string): string => {
    const segments = getMeaningfulSegments(pathname);
    if (segments.length === 0) {
        return 'Dashboard';
    }

    const sectionTokens = new Set(['portal', 'links', 'investor']);
    const leaf = [...segments]
        .reverse()
        .find((segment) => !isRouteParameter(segment) && !sectionTokens.has(segment));

    if (leaf) {
        return titleize(leaf);
    }

    return titleize(segments[0]);
};
